import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  PARTNER_TIERS,
  PARTNER_TIER_LABEL,
  PARTNER_TYPES,
  PARTNER_TYPE_LABEL,
  PORTAL_CONTENT_KEYS,
  type PortalContentForKey,
  type PortalContentKey,
  type PortalContentRow,
  type PortalContentScopeKind,
  type PortalContentWriteInput,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCreatePortalContent } from "./use-create-portal-content";
import { useUpdatePortalContent } from "./use-update-portal-content";
import {
  LevelUpFields,
  SupportLinksFields,
  WelcomeFields,
  emptyContentForKey,
} from "./portal-content-form-fields";

type Mode =
  | { kind: "create"; defaultKey?: PortalContentKey }
  | { kind: "edit"; row: PortalContentRow };

const KEY_LABELS: Record<PortalContentKey, string> = {
  welcome: "Welcome banner",
  level_up: "Level-up matrix",
  support_links: "Support links",
};

export function PortalContentDialog({
  open,
  onOpenChange,
  mode,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  mode: Mode;
}) {
  const createMutation = useCreatePortalContent();
  const updateMutation = useUpdatePortalContent();

  const [key, setKey] = useState<PortalContentKey>(
    mode.kind === "edit" ? mode.row.key : mode.defaultKey ?? "welcome",
  );
  const [scopeKind, setScopeKind] = useState<PortalContentScopeKind>(
    mode.kind === "edit" ? mode.row.scope_kind : "all",
  );
  const [scopeValue, setScopeValue] = useState<string | null>(
    mode.kind === "edit" ? mode.row.scope_value : null,
  );
  const [position, setPosition] = useState<number>(
    mode.kind === "edit" ? mode.row.position : 0,
  );
  const [active, setActive] = useState<boolean>(
    mode.kind === "edit" ? mode.row.active : true,
  );
  const [content, setContent] = useState<PortalContentForKey[PortalContentKey]>(
    mode.kind === "edit" ? mode.row.content : emptyContentForKey(key),
  );

  useEffect(() => {
    if (mode.kind === "create") {
      setContent(emptyContentForKey(key));
    }
  }, [key, mode.kind]);

  useEffect(() => {
    if (scopeKind === "all") {
      setScopeValue(null);
    } else if (
      scopeKind === "tier" &&
      (!scopeValue || !(PARTNER_TIERS as readonly string[]).includes(scopeValue))
    ) {
      setScopeValue(PARTNER_TIERS[0]);
    } else if (
      scopeKind === "partner_type" &&
      (!scopeValue || !(PARTNER_TYPES as readonly string[]).includes(scopeValue))
    ) {
      setScopeValue(PARTNER_TYPES[0]);
    }
  }, [scopeKind, scopeValue]);

  const pending = createMutation.isPending || updateMutation.isPending;

  function submit() {
    // Build the discriminated-union payload so TS can infer the right
    // scope_value type per scope_kind. The form-state pair is loose by
    // design (the inputs are independent); the cast collapses them.
    const payload = {
      key,
      position,
      active,
      content,
      ...(scopeKind === "all"
        ? { scope_kind: "all" as const, scope_value: null }
        : scopeKind === "tier"
          ? {
              scope_kind: "tier" as const,
              scope_value:
                ((scopeValue ?? PARTNER_TIERS[0]) as (typeof PARTNER_TIERS)[number]),
            }
          : {
              scope_kind: "partner_type" as const,
              scope_value:
                ((scopeValue ?? PARTNER_TYPES[0]) as (typeof PARTNER_TYPES)[number]),
            }),
    } satisfies PortalContentWriteInput;
    if (mode.kind === "create") {
      createMutation.mutate(payload, {
        onSuccess: () => {
          toast.success("Content row created");
          onOpenChange(false);
        },
      });
    } else {
      updateMutation.mutate(
        { id: mode.row.id, input: payload },
        {
          onSuccess: () => {
            toast.success("Content row saved");
            onOpenChange(false);
          },
        },
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {mode.kind === "create"
              ? "New portal content row"
              : "Edit portal content"}
          </DialogTitle>
          <DialogDescription>
            Author the JSONB row that backs a partner-facing portal section.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Key</Label>
              <Select
                value={key}
                onValueChange={(v) => setKey(v as PortalContentKey)}
                disabled={mode.kind === "edit"}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PORTAL_CONTENT_KEYS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {KEY_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mode.kind === "edit" ? (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Key is fixed after creation — different keys have different
                  shapes.
                </p>
              ) : null}
            </div>
            <div>
              <Label>Position</Label>
              <Input
                type="number"
                min={0}
                value={position}
                onChange={(e) =>
                  setPosition(Number.parseInt(e.target.value || "0", 10) || 0)
                }
              />
              <p className="mt-1 text-[11px] text-muted-foreground">
                Lowest wins when multiple rows share the same scope+key.
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Audience scope</Label>
              <Select
                value={scopeKind}
                onValueChange={(v) => setScopeKind(v as PortalContentScopeKind)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All partners</SelectItem>
                  <SelectItem value="tier">By tier</SelectItem>
                  <SelectItem value="partner_type">By partner type</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scopeKind !== "all" ? (
              <div>
                <Label>Scope value</Label>
                <Select
                  value={scopeValue ?? ""}
                  onValueChange={(v) => setScopeValue(v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {scopeKind === "tier"
                      ? PARTNER_TIERS.map((t) => (
                          <SelectItem key={t} value={t}>
                            {PARTNER_TIER_LABEL[t]}
                          </SelectItem>
                        ))
                      : PARTNER_TYPES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {PARTNER_TYPE_LABEL[t]}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-between rounded-md border bg-muted/20 px-3 py-2">
            <div>
              <Label htmlFor="active-toggle" className="text-sm">
                Active
              </Label>
              <p className="text-[11px] text-muted-foreground">
                Inactive rows are hidden from partners and excluded from
                resolution.
              </p>
            </div>
            <Switch id="active-toggle" checked={active} onCheckedChange={setActive} />
          </div>

          <div className="rounded-md border bg-card p-4">
            {key === "welcome" ? (
              <WelcomeFields
                value={content as PortalContentForKey["welcome"]}
                onChange={(next) => setContent(next)}
              />
            ) : key === "level_up" ? (
              <LevelUpFields
                value={content as PortalContentForKey["level_up"]}
                onChange={(next) => setContent(next)}
              />
            ) : (
              <SupportLinksFields
                value={content as PortalContentForKey["support_links"]}
                onChange={(next) => setContent(next)}
              />
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            Cancel
          </Button>
          <Button onClick={submit} disabled={pending}>
            {pending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
            {mode.kind === "create" ? "Create" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
